import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Paper,
    CircularProgress,
    Backdrop,
    Button,
    Grid,
    Card,
    CardContent,
    Divider,
    Chip,
} from "@mui/material";
import {
    ArrowBack,
    Description,
} from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import axiosInstance from "../../api/axiosInstance";
import base_url from "../../constants";
import useNotification from "../common/Notification";
import { formatTemplateForPreview } from "../../utils/templatePreviewUtils";
import TemplateDetailsPreview from "./template/TemplateDetailsPreview";
import { WB_SX } from "../../ui/ux/uiTokens";

const TemplateDetails = () => {
    const { templateid } = useParams();
    const navigate = useNavigate();
    const notify = useNotification();

    const [loading, setLoading] = useState(true);
    const [templateData, setTemplateData] = useState(null);
    const [formattedTemplate, setFormattedTemplate] = useState(null);

    useEffect(() => {
        fetchTemplateDetails();
    }, [templateid]);

    const fetchTemplateDetails = async () => {
        if (!templateid) {
            notify("Template ID is required", "error");
            navigate("/whatsapp-business/templates");
            return;
        }

        setLoading(true);
        try {
            const response = await axiosInstance.get(
                `${base_url}/whatsapp-business/template/${templateid}/details`,
                {
                    params: {
                        fields: 'id,name,language,category,status,components,previous_category,parameter_format'
                    }
                }
            );

            if (response.data) {
                setTemplateData(response.data);
                // Format template for preview
                const formatted = formatTemplateForPreview(response.data);
                setFormattedTemplate(formatted);
            } else {
                notify("Failed to fetch template details", "error");
            }
        } catch (error) {
            console.error("Error fetching template details:", error);
            notify(
                error.response?.data?.error || "Failed to fetch template details",
                "error"
            );
            navigate("/whatsapp-business/templates");
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Backdrop
                sx={(theme) => ({
                    color: "#fff",
                    zIndex: theme.zIndex.drawer + 1,
                })}
                open={loading}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
        );
    }

    if (!templateData) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography variant="h6" color="error">
                    Template not found
                </Typography>
                <Button
                    variant="outlined"
                    startIcon={<ArrowBack />}
                    onClick={() => navigate("/whatsapp-business/templates")}
                    sx={{ mt: 2 }}
                >
                    Back to Templates
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={WB_SX.page}>
            {/* Header */}
            <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                <Button
                    variant="outlined"
                    startIcon={<ArrowBack />}
                    onClick={() => navigate("/whatsapp-business/templates")}
                    sx={WB_SX.secondaryButton}
                >
                    Back
                </Button>
                <Typography variant="h5" sx={{ ...WB_SX.pageTitle, mb: 0 }}>
                    Template Details
                </Typography>
            </Box>

            <Grid container spacing={3}>
                {/* Left Side - Template Information */}
                <Grid item xs={12} md={7}>
                    <Card elevation={0} sx={WB_SX.card}>
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 2, ...WB_SX.sectionTitle }}>
                                Template Information
                            </Typography>
                            
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Template Name
                                    </Typography>
                                    <Typography variant="body1" fontWeight="bold">
                                        {templateData.name || "N/A"}
                                    </Typography>
                                </Box>

                                <Divider />

                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Template ID
                                    </Typography>
                                    <Typography 
                                        variant="body2" 
                                        sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
                                    >
                                        {templateData.id || "N/A"}
                                    </Typography>
                                </Box>

                                <Divider />

                                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Category
                                        </Typography>
                                        <Chip
                                            label={templateData.category || "N/A"}
                                            size="small"
                                            sx={{
                                                mt: 0.5,
                                                fontWeight: 700,
                                                backgroundColor:
                                                    templateData.category === "AUTHENTICATION"
                                                        ? "#e8f5e9"
                                                        : templateData.category === "MARKETING"
                                                        ? "#fff3e0"
                                                        : "#e3f2fd",
                                                color:
                                                    templateData.category === "AUTHENTICATION"
                                                        ? "#2e7d32"
                                                        : templateData.category === "MARKETING"
                                                        ? "#ed6c02"
                                                        : "#1976d2",
                                            }}
                                        />
                                    </Box>
                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Language
                                        </Typography>
                                        <Chip
                                            label={templateData.language || "N/A"}
                                            color="secondary"
                                            size="small"
                                            sx={{ mt: 0.5 }}
                                        />
                                    </Box>
                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Status
                                        </Typography>
                                        <Chip
                                            label={templateData.status || "N/A"}
                                            color={
                                                templateData.status === 'APPROVED' 
                                                    ? 'success' 
                                                    : templateData.status === 'PENDING'
                                                    ? 'warning'
                                                    : templateData.status === 'REJECTED'
                                                    ? 'error'
                                                    : 'default'
                                            }
                                            size="small"
                                            sx={{ mt: 0.5 }}
                                        />
                                    </Box>
                                </Box>

                                {/* Components Details */}
                                {templateData.components && templateData.components.length > 0 && (
                                    <>
                                        <Divider sx={{ my: 2 }} />
                                        <Box>
                                            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                                                Components
                                            </Typography>
                                            {templateData.components.map((component, index) => (
                                                <Paper
                                                    key={index}
                                                    variant="outlined"
                                                    sx={{ p: 2, mb: 2 }}
                                                >
                                                    <Typography variant="subtitle2" fontWeight="bold">
                                                        {component.type}
                                                    </Typography>
                                                    {component.format && (
                                                        <Chip
                                                            label={`Format: ${component.format}`}
                                                            size="small"
                                                            sx={{ mt: 1 }}
                                                        />
                                                    )}
                                                    {component.text && (
                                                        <Typography
                                                            variant="body2"
                                                            sx={{ mt: 1, whiteSpace: "pre-wrap" }}
                                                        >
                                                            {component.text}
                                                        </Typography>
                                                    )}
                                                    {component.buttons && (
                                                        <Box sx={{ mt: 1 }}>
                                                            <Typography variant="caption" color="text.secondary">
                                                                Buttons:
                                                            </Typography>
                                                            {component.buttons.map((button, btnIndex) => (
                                                                <Chip
                                                                    key={btnIndex}
                                                                    label={`${button.type}: ${button.text || 'N/A'}`}
                                                                    size="small"
                                                                    sx={{ mt: 0.5, mr: 0.5 }}
                                                                />
                                                            ))}
                                                        </Box>
                                                    )}
                                                </Paper>
                                            ))}
                                        </Box>
                                    </>
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Right Side - Preview */}
                <Grid item xs={12} md={5}>
                    <Box sx={{ position: "sticky", top: 20 }}>
                        <Typography variant="h6" sx={{ mb: 2, ...WB_SX.sectionTitle }}>
                            WhatsApp Preview
                        </Typography>
                        <TemplateDetailsPreview formattedTemplate={formattedTemplate} />
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default TemplateDetails;

